import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { fadeIn } from 'atoms/animations/keyframes';
import { Button } from 'atoms/buttons/Button';
import { SorareLogo } from 'atoms/icons/SorareLogo';
import { Vertical } from 'atoms/layout/flex';
import { DisplayS, HeadlineL } from 'atoms/typography';
import { glossary } from 'lib/glossary';

type GameResult = 'Win' | 'Lose' | 'Tie';

const Root = styled(Vertical)`
  visibility: hidden;
  opacity: 0;
  &.displayResult {
    visibility: visible;
    animation: 1s ease ${fadeIn} forwards;
  }
`;

const Result = styled(Vertical)`
  padding: var(--double-unit) var(--triple-unit);
  max-width: 320px;
  width: 100%;
  border-radius: var(--unit);
  overflow: hidden;
  background-size: cover;
  background-position: center;
`;

type Props = {
  onConfirm: () => void;
  popupBackground: string;
  result: GameResult;
  displayResult: boolean;
};

export const MinigamePopup = ({
  onConfirm,
  popupBackground,
  result,
  displayResult,
}: Props) => {
  const getGameResultMessage = (): ReactNode => {
    let gameResult;
    switch (result) {
      case 'Win':
        gameResult = (
          <Vertical gap={1} center>
            <>
              <DisplayS bold className="text-center">
                <FormattedMessage
                  id="football.minigames.pve_.result.title.wellDone"
                  defaultMessage="Well done!"
                />
              </DisplayS>
              <HeadlineL className="text-center">
                <FormattedMessage
                  id="football.minigames.pve_.title.getAGift"
                  defaultMessage="Get a gift"
                />
              </HeadlineL>
            </>
            <Button color="secondary" size="large" onClick={() => onConfirm()}>
              <FormattedMessage {...glossary.playNow} />
            </Button>
          </Vertical>
        );
        return gameResult;
      case 'Lose':
        gameResult = (
          <Vertical gap={1} center>
            <>
              <DisplayS bold className="text-center">
                <FormattedMessage
                  id="football.minigames.pve_.result.title.tooBad"
                  defaultMessage="Too bad."
                />
              </DisplayS>
              <HeadlineL className="text-center">
                <FormattedMessage
                  id="football.minigames.pve_.result.title.keepPlaying"
                  defaultMessage="Want to keep playing?"
                />
              </HeadlineL>
            </>
            <Button color="secondary" size="large" onClick={() => onConfirm()}>
              <FormattedMessage
                id="football.minigames.pve_.button.unlockTheFullVersion"
                defaultMessage="Unlock the full version"
              />
            </Button>
          </Vertical>
        );
        return gameResult;
      default:
        gameResult = (
          <Vertical gap={1} center>
            <>
              <DisplayS bold className="text-center">
                <FormattedMessage
                  id="football.minigames.pve_.result.title.oops"
                  defaultMessage="Oops!"
                />
              </DisplayS>
              <HeadlineL className="text-center">
                <FormattedMessage
                  id="football.minigames.pve_.result.title.funStopsHere"
                  defaultMessage="Looks like the fun stops here!"
                />
              </HeadlineL>
            </>
            <Button color="secondary" size="large" onClick={() => onConfirm()}>
              <FormattedMessage
                id="football.minigames.pve_.button.unlockTheFullVersion"
                defaultMessage="Unlock the full version"
              />
            </Button>
          </Vertical>
        );
    }
    return gameResult;
  };

  return (
    <Root center className={classNames({ displayResult })}>
      <Result
        center
        gap={2}
        style={{ backgroundImage: `url(${popupBackground})` }}
      >
        <SorareLogo />
        {getGameResultMessage()}
      </Result>
    </Root>
  );
};
